/**
 * 햄버거 메뉴 버튼 컴포넌트
 * 반응형 사이드바 토글을 위한 햄버거 메뉴
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { colors, spacing, borderRadius, shadows, animations, zIndex } from '../../styles/designTokens';

// ===== 타입 정의 =====
export interface HamburgerMenuProps {
    isOpen: boolean;
    onClick: () => void;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'default' | 'minimal' | 'floating';
}

// ===== 스타일드 컴포넌트 =====
const HamburgerButton = styled.button<{
    $isOpen: boolean;
    $size: 'sm' | 'md' | 'lg';
    $variant: 'default' | 'minimal' | 'floating';
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${({ $variant }) => {
        switch ($variant) {
            case 'minimal': return 'transparent';
            case 'floating': return colors.semantic.background.primary;
            default: return colors.semantic.background.primary;
        }
    }};
  border: ${({ $variant }) => {
        switch ($variant) {
            case 'minimal': return 'none';
            case 'floating': return `1px solid ${colors.semantic.border.primary}`;
            default: return `1px solid ${colors.semantic.border.primary}`;
        }
    }};
  border-radius: ${({ $variant }) => {
        switch ($variant) {
            case 'minimal': return 'none';
            case 'floating': return borderRadius.full;
            default: return borderRadius.lg;
        }
    }};
  cursor: pointer;
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  position: relative;
  z-index: ${zIndex.overlayPanel};
  
  /* 크기별 스타일 */
  ${({ $size }) => {
        switch ($size) {
            case 'sm':
                return css`
          width: 32px;
          height: 32px;
          padding: ${spacing[2]};
        `;
            case 'md':
                return css`
          width: 40px;
          height: 40px;
          padding: ${spacing[3]};
        `;
            case 'lg':
                return css`
          width: 48px;
          height: 48px;
          padding: ${spacing[4]};
        `;
        }
    }}
  
  /* 호버 효과 */
  &:hover {
    background: ${({ $variant }) => {
        switch ($variant) {
            case 'minimal': return colors.semantic.background.tertiary;
            case 'floating': return colors.semantic.background.secondary;
            default: return colors.semantic.background.secondary;
        }
    }};
    transform: scale(1.05);
    box-shadow: ${({ $variant }) => {
        switch ($variant) {
            case 'minimal': return 'none';
            case 'floating': return shadows.md;
            default: return shadows.sm;
        }
    }};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  /* 포커스 스타일 */
  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover {
      transform: none !important;
    }
  }
`;

const HamburgerLine = styled.span<{ $isOpen: boolean; $size: 'sm' | 'md' | 'lg' }>`
  display: block;
  background-color: ${colors.semantic.text.primary};
  border-radius: 2px;
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  
  /* 크기별 스타일 */
  ${({ $size }) => {
        switch ($size) {
            case 'sm':
                return css`
          width: 16px;
          height: 2px;
          margin: 1px 0;
        `;
            case 'md':
                return css`
          width: 20px;
          height: 2px;
          margin: 2px 0;
        `;
            case 'lg':
                return css`
          width: 24px;
          height: 3px;
          margin: 2px 0;
        `;
        }
    }}
  
  /* 애니메이션 */
  ${({ $isOpen }) => {
        if ($isOpen) {
            return css`
        &:nth-child(1) {
          transform: rotate(45deg) translate(5px, 5px);
        }
        
        &:nth-child(2) {
          opacity: 0;
          transform: translateX(20px);
        }
        
        &:nth-child(3) {
          transform: rotate(-45deg) translate(7px, -6px);
        }
      `;
        }
    }}
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

// ===== 메인 컴포넌트 =====
export const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
    isOpen,
    onClick,
    size = 'md',
    variant = 'default'
}) => {
    return (
        <HamburgerButton
            $isOpen={isOpen}
            $size={size}
            $variant={variant}
            onClick={onClick}
            aria-label={isOpen ? '메뉴 닫기' : '메뉴 열기'}
            aria-expanded={isOpen}
        >
            <HamburgerLine $isOpen={isOpen} $size={size} />
            <HamburgerLine $isOpen={isOpen} $size={size} />
            <HamburgerLine $isOpen={isOpen} $size={size} />
        </HamburgerButton>
    );
};

export default HamburgerMenu;
